import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { motion, type Variants } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { faCalendar } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { Button } from '@components/main-components/button/Button';
import { ChannelAboutFragmentDoc, type FragmentType, getFragment, gql } from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';

const MAX_LINE_COUNT = 3;
const LINE_CLAMP_CLASSES = 'line-clamp-3';
export const LINE_HEIGHT = 18;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ChannelAbout on Channel {
    description
    createdAt
    twitterUrl
    telegramUrl
    discordUrl
    tiktokUrl
    websiteUrl
  }
`);

export function ChannelAbout({
  channelFrag,
}: {
  channelFrag: FragmentType<ChannelAboutFragmentDoc>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [clampClass, setClampClass] = useState(LINE_CLAMP_CLASSES);
  const [contentHeight, setContentHeight] = useState<number | undefined>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const maxContentHeight = LINE_HEIGHT * MAX_LINE_COUNT;

  const hide = !!contentHeight && contentHeight > maxContentHeight;

  const onExpand = useStableCallback(() => {
    setExpanded(prev => {
      if (!prev) {
        setClampClass('');
      }
      return !prev;
    });
  });

  const onAnimationComplete = useStableCallback(() => {
    if (expanded) {
      setClampClass('');
    } else {
      setClampClass(LINE_CLAMP_CLASSES);
    }
  });

  const animationVariants: Variants = {
    expand: {
      height: 'auto',
      overflow: 'visible',
      transition: { duration: 0.2, delay: 0.1 },
    },
    collapse: {
      height: hide ? maxContentHeight : 'auto',
      overflow: 'hidden',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  useEffect(() => {
    setContentHeight(contentRef.current?.scrollHeight);
  }, [contentRef, contentRef.current?.scrollHeight]);

  const channel = getFragment(ChannelAboutFragmentDoc, channelFrag);

  const formattedDate = useMemo(
    () => format(new Date(channel.createdAt), 'MMM Y'),
    [channel.createdAt],
  );

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-title text-title-s font-medium text-base800">About</h2>

      <motion.span
        ref={contentRef}
        animate={expanded ? 'expand' : 'collapse'}
        variants={animationVariants}
        transition={{ ease: 'easeOut' }}
        className={twMerge('font-base !text-base-m font-normal text-base700', clampClass)}
        onAnimationComplete={onAnimationComplete}
      >
        {channel.description}
      </motion.span>
      {!!hide && (
        <Button
          label={!expanded ? 'See more' : 'See less'}
          variant="text"
          onClick={onExpand}
          size="S"
          className="-mx-1 h-auto px-1 py-0 font-base !text-[12px] font-semibold leading-[16px] text-base900"
        />
      )}

      <div className="flex w-full items-center gap-2">
        <FontAwesomeIcon icon={faCalendar} className="leading-[24px] text-base400" fontSize={14} />

        <span className="font-base text-[14px] font-medium leading-[24px] text-base400">
          Created {formattedDate}
        </span>
      </div>
    </div>
  );
}
